import { Combobox } from '@headlessui/react'
import { useEffect, useState } from 'react'

import { type PlaceResult, type RadiusValue } from '@/lib/googlePlaces'
import { RADIUS_OPTIONS } from '@/lib/googlePlaces'
import { OfficeDataCache } from '@/lib/officeCache'
import { getOfficeCoordinateCache } from '@/lib/officeCoordinateCache'

// import { searchParamsData } from '@/lib/sbAPI'
// import { getStoryblokApi } from '@/lib/storyblok-api'
import MapIcon from '../../../../public/svg/find-an-office.svg'
import styles from './Officeinputsearch.module.css'

// --- Interface Definitions (Kept for completeness) ---

interface OfficeResult {
  id: string
  name: string
  postcode?: string
  suburb?: string
  office: string
  url: string
  distance?: number
}

// interface StoryblokStory {
//   id: string
//   name: string
//   full_slug: string
//   content: {
//     component: string
//     office?: Array<{
//       address?: string
//       location?: Array<{
//         latitude: string
//         longitude: string
//       }>
//     }>
//     postcode?: string
//     suburb?: string
//     city?: string
//     Latitude?: string
//     Longitude?: string
//     [key: string]: unknown
//   }
//   [key: string]: unknown
// }

// interface StoryblokResponse {
//   stories: StoryblokStory[]
// }

export type OfficeinputsearchProps = {
  placeholder?: string
  width?: string | null
  variant?: 'primary' | 'secondary'
  hardCodedResults?: OfficeResult[]
  onSelect?: (item: OfficeResult) => void
  selectValue?: string
  onReset?: () => void
  enablePlacesAPI?: boolean
  searchRadius?: RadiusValue
  onRadiusChange?: (radius: RadiusValue) => void
  showRadiusSelector?: boolean
}

// --- Component Start ---

function PhnxOfficeinputsearch({
  placeholder = 'Enter a postcode or city name',
  width = '100%',
  variant = 'primary',
  hardCodedResults = [],
  onSelect,
  enablePlacesAPI = false,
  searchRadius = 20,
  onRadiusChange,
  showRadiusSelector = false,
}: OfficeinputsearchProps) {
  const [results, setResults] = useState<OfficeResult[]>([])
  const [query, setQuery] = useState('')
  const [debouncedQuery, setDebouncedQuery] = useState('')
  const [selectedOffice, setSelectedOffice] = useState<OfficeResult | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  // Removed: isFocused state
  // Removed: inputRef
  const [lastSelectedPlace, setLastSelectedPlace] = useState<PlaceResult | null>(null)

  const [internalRadius, setInternalRadius] = useState<RadiusValue>(searchRadius)

  // Sync internal radius with prop changes
  useEffect(() => {
    setInternalRadius(searchRadius)
  }, [searchRadius])

  // --- helper function ---
  const fetchAndFilterOffices = async (
    lat: number,
    lng: number
    // suburb?: string,
    // postcode?: string
  ) => {
    try {
      const location = process.env.NEXT_PUBLIC_NEXT_LOCATION ?? 'au'
      const filterLocation = location === 'en-nz' ? 'au' : 'en-nz'

      const allOffices = await OfficeDataCache.getAllOffices(location, filterLocation)
      const allOfficesData = { stories: allOffices }

      // OPTIMIZATION: Use existing Latitude/Longitude from Storyblok instead of geocoding
      // This eliminates 50+ API calls per search!
      const officesWithDistance: Array<{
        story: (typeof allOfficesData.stories)[0]
        estimatedDistance: number
        postcode: string
      }> = []

      for (const story of allOfficesData.stories ?? []) {
        let officeLat: number | null = null
        let officeLng: number | null = null
        let officePostcode = ''

        if (story.content.component === 'office') {
          // Get coordinates from Latitude/Longitude fields
          officeLat = story.content.Latitude ? parseFloat(story.content.Latitude) : null
          officeLng = story.content.Longitude ? parseFloat(story.content.Longitude) : null
          officePostcode = story.content.postcode ?? ''
        } else if (story.content.component === 'advanceOffice') {
          // Try to get from location array
          const location = story.content.office?.[0]?.location?.[0]
          if (location) {
            officeLat = location.latitude ? parseFloat(location.latitude) : null
            officeLng = location.longitude ? parseFloat(location.longitude) : null
          }
          // Extract postcode from address
          const address = story.content.office?.[0]?.address ?? ''
          let match = address.match(/(?:VIC|NSW|QLD|SA|WA|TAS|NT|ACT)\s+(\d{4})/i)
          if (match) {
            officePostcode = match[1]
          } else {
            match = address.match(/(\d{4})\s*$/i)
            if (match) officePostcode = match[1]
          }
        }

        // Only include offices with valid coordinates
        if (officeLat !== null && officeLng !== null && !isNaN(officeLat) && !isNaN(officeLng)) {
          const distance = calculateDistance(lat, lng, officeLat, officeLng)
          if (distance <= internalRadius) {
            officesWithDistance.push({
              story,
              estimatedDistance: Math.round(distance * 10) / 10,
              postcode: officePostcode,
            })
          }
        }
      }

      // format the results to your OfficeResult type
      return officesWithDistance
        .map(({ story, estimatedDistance, postcode }) => {
          let postcodeDisplay: string | undefined
          let suburb: string | undefined

          if (story.content.component === 'advanceOffice') {
            const address = story.content.office?.[0]?.address ?? ''
            const match = address.match(/(\d{4})$/)
            postcodeDisplay = match?.[1] ?? postcode ?? undefined
            suburb = address.split(',')[0]?.trim() ?? undefined
          } else if (story.content.component === 'office') {
            postcodeDisplay = story.content.postcode ?? undefined
            suburb = story.content.suburb ?? story.content.city ?? undefined
          }

          return {
            id: story.id,
            name: `${story.name}${postcodeDisplay != null && postcodeDisplay !== '' ? `, ${postcodeDisplay}` : ''}`,
            postcode: postcodeDisplay,
            suburb,
            office: story.name,
            url: '/' + story.full_slug,
            distance: estimatedDistance,
          }
        })
        .sort((a, b) => (a.distance ?? 999) - (b.distance ?? 999))
    } catch (error) {
      console.error('Failed to fetch offices:', error)
      return []
    }
  }

  // -------------------------------------------------------------------
  // 1. Debounce and Search Reset Logic (CRUCIAL MODIFICATION)
  // -------------------------------------------------------------------
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setDebouncedQuery(query)
    }, 300)

    // ✨ MODIFIED LOGIC: Check if the query is truly empty (NOT our temporary space ' ')
    const isTrulyEmpty = query.trim().length === 0 && query !== ' '

    if (isTrulyEmpty) {
      setResults([])
      setLastSelectedPlace(null)
      setSelectedOffice(null)
    }

    return () => clearTimeout(timeoutId)
  }, [query])

  // -------------------------------------------------------------------
  // 2. Clear Temporary Query Logic (NEW EFFECT)
  // -------------------------------------------------------------------
  useEffect(() => {
    // Only run if the query is the temporary placeholder and we have results
    if (query === ' ' && results.length > 0) {
      // Use a short delay to allow the DOM to render the new results fully
      const timer = setTimeout(() => {
        // Clear the query, relying on lastSelectedPlace to keep the conditional true
        setQuery('')
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [query, results.length])

  // -------------------------------------------------------------------
  // 3. Search Effect (Kept as is)
  // -------------------------------------------------------------------
  useEffect(() => {
    if (hardCodedResults.length > 0) {
      setResults(hardCodedResults)
      return
    }

    const trimmedQuery = debouncedQuery.trim()

    // Note: The logic in the debouncing useEffect (Step 1) handles the query === '' case.
    if (!trimmedQuery) {
      setResults([])
      return
    }
    // ... rest of the search logic (performSearch function) ...
    // NOTE: This logic remains unchanged. It relies on debouncedQuery.

    const searchOffices = async () => {
      try {
        setIsLoading(true)
        const trimmedQuery = debouncedQuery.trim()

        // Client-side validation: enforce minimum length BEFORE any API calls
        const isPostcode = /^\d+$/.test(trimmedQuery)
        const minLength = isPostcode ? 4 : 3

        if (trimmedQuery.length < minLength) {
          setResults([])
          setIsLoading(false)
          return
        }

        let lat = 0
        let lng = 0
        let suburb: string | undefined
        let postcode: string | undefined

        // COST OPTIMIZATION: Use Geocoding API directly for both postcodes AND suburbs
        // Geocoding API: $5/1000 requests
        // vs Place Details API: $17/1000 requests (3.4x more expensive!)

        const country =
          process.env.NEXT_PUBLIC_NEXT_LOCATION === 'en-nz' ? 'New Zealand' : 'Australia'

        if (isPostcode && trimmedQuery.length === 4) {
          // Direct postcode geocoding
          const coords = await getOfficeCoordinateCache().geocodePostcode(trimmedQuery, country)
          if (coords) {
            lat = coords.lat
            lng = coords.lng
            postcode = trimmedQuery
          }
        } else {
          // For suburb/city names, use Geocoding API directly (cheaper than Places API)
          // This geocodes "suburb, country" format which works well for AU/NZ suburbs
          const coords = await getOfficeCoordinateCache().geocodePostcode(trimmedQuery, country)
          if (coords) {
            lat = coords.lat
            lng = coords.lng
            suburb = trimmedQuery
          }
        }

        if (!lat && !lng) {
          setResults([])
          return
        }

        const offices = await fetchAndFilterOffices(lat, lng)
        setResults(
          offices.length
            ? offices
            : [
                {
                  id: 'no-results',
                  name: `No offices found within ${internalRadius}km of ${suburb ?? postcode ?? 'unknown location'}`,
                  postcode,
                  suburb,
                  office: 'No offices',
                  url: '',
                },
              ]
        )
      } finally {
        setIsLoading(false)
      }
    }

    searchOffices()
  }, [debouncedQuery])

  // -------------------------------------------------------------------
  // 4. Distance and handlePlaceSelect Logic (Kept as is)
  // -------------------------------------------------------------------
  const handleOnSelect = (item: OfficeResult | null) => {
    if (item && onSelect) {
      onSelect(item)
    } else if (item) {
      window.location.href = item.url
    }
  }

  const handleClear = () => {
    setQuery('')
    setResults([])
    setSelectedOffice(null)
    setLastSelectedPlace(null)
  }

  const calculateDistance = (lat1: number, lng1: number, lat2: number, lng2: number): number => {
    const R = 6371
    const dLat = ((lat2 - lat1) * Math.PI) / 180
    const dLng = ((lng2 - lng1) * Math.PI) / 180
    const a =
      Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos((lat1 * Math.PI) / 180) *
        Math.cos((lat2 * Math.PI) / 180) *
        Math.sin(dLng / 2) *
        Math.sin(dLng / 2)
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
    return R * c
  }

  useEffect(() => {
    if (selectedOffice) {
      setQuery('')
      setResults([])
    }
  }, [selectedOffice])

  // -------------------------------------------------------------------
  // 6. Component Render
  // -------------------------------------------------------------------
  return (
    <div className={`${styles['officeinputsearch']} ${styles[`variant-${variant}`]}`}>
      <div style={{ width: width ?? '100%' }}>
        <Combobox
          value={selectedOffice}
          onChange={(value) => {
            setSelectedOffice(value)
            handleOnSelect(value)
          }}
        >
          <div className="relative">
            <div className="flex gap-2 items-center">
              <div className="flex-1">
                <Combobox.Input
                  // Removed: ref={inputRef}
                  className={styles.search}
                  placeholder={placeholder}
                  value={query}
                  onChange={(event) => setQuery(event.target.value)}
                  displayValue={(office: OfficeResult | null) => office?.name ?? ''}
                  // Removed: onFocus and onBlur logic
                />
              </div>

              {enablePlacesAPI && showRadiusSelector && onRadiusChange && (
                <div className={styles.radiusSelector}>
                  <select
                    value={internalRadius}
                    onChange={async (e) => {
                      const newRadius = Number(e.target.value) as RadiusValue
                      setInternalRadius(newRadius)
                      onRadiusChange(newRadius)
                      setQuery('')
                      setSelectedOffice(null)
                    }}
                    className={styles.radiusSelect}
                  >
                    {RADIUS_OPTIONS.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </div>

            {/* ✨ CONDITIONAL RENDER: This must be met to re-insert the element */}
            {(query.trim().length > 0 || lastSelectedPlace) && (
              <Combobox.Options className={styles.options}>
                {isLoading ? (
                  <div className={styles.option}>
                    <span className={styles.result}>Searching...</span>
                  </div>
                ) : results.length > 0 ? (
                  results.map((item) => (
                    <Combobox.Option
                      key={item.id}
                      value={item}
                      className={`${styles.option} data-[selected]:${styles.optionActive}`}
                    >
                      <span className={styles.result}>
                        <div className={styles.leftContent}>
                          <MapIcon />
                          <span>{item.name}</span>
                        </div>
                        {item.distance !== undefined && item.distance < 999 && (
                          <span className={styles.distance}>{item.distance}km</span>
                        )}
                      </span>
                    </Combobox.Option>
                  ))
                ) : (
                  <div className={styles.option}>
                    <span className={styles.result}>No offices found</span>
                  </div>
                )}
              </Combobox.Options>
            )}
          </div>
        </Combobox>

        {/* Removed: isFocused check from the clear button logic */}
        {query.trim() && (
          <button
            type="button"
            className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer hover:opacity-70"
            onClick={handleClear}
            style={{ zIndex: 1000 }}
            aria-label="Clear search"
          >
            <svg
              width="20"
              height="20"
              focusable="false"
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              style={{ fill: variant === 'secondary' ? 'black' : 'white' }}
            >
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.58 12 5 17.58 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"></path>
            </svg>
          </button>
        )}
      </div>
    </div>
  )
}

export { PhnxOfficeinputsearch }
export default PhnxOfficeinputsearch
